import type { ServiceOrderFormValues, ServiceOrderInvoicesResult, ServiceOrderView } from "@cx/contracts";
import { serviceOrderTotals, type ServiceOrderItemKind, type ServiceOrderTotals } from "@cx/domain";

// ===== Ordens de serviço: estado do formulário e conversões (RF-SER-03) =====

/** Linha do formulário. Peça: produto do cadastro; preço vazio = preço de venda do produto (o servidor resolve). */
export type OsItemRow = {
  key: string;
  kind: ServiceOrderItemKind;
  productId: string | null;
  description: string;
  code: string;
  unit: string;
  qtyQ4: number | null;
  unitPriceCents: number | null;
  toQuote: boolean;
  done: boolean;
};

export type ServiceOrderForm = {
  contact: { id: string; name: string } | null;
  sellerId: string;
  storeId: string;
  technician: string;
  equipment: string;
  serialNumber: string;
  problem: string;
  receptionNotes: string;
  internalNotes: string;
  entryDate: string;
  expectedDate: string;
  finishDate: string;
  exitDate: string;
  startTime: string;
  endTime: string;
  items: OsItemRow[];
  freightCents: number | null;
  discountCents: number | null;
  quoteMode: boolean;
  quoted: boolean;
  warranty: string;
  warrantyInfo: string;
  serviceNotes: string;
  technicianInfo: string;
  paymentTermId: string;
  paymentMethodId: string;
  financialCategoryId: string;
  warehouseId: string;
};

let seq = 0;
export const osRowKey = () => `s${++seq}`;

export function emptyServiceOrderForm(today: string, sellerId = ""): ServiceOrderForm {
  return {
    contact: null,
    sellerId,
    storeId: "",
    technician: "",
    equipment: "",
    serialNumber: "",
    problem: "",
    receptionNotes: "",
    internalNotes: "",
    entryDate: today,
    expectedDate: "",
    finishDate: "",
    exitDate: "",
    startTime: "",
    endTime: "",
    items: [],
    freightCents: null,
    discountCents: null,
    quoteMode: false,
    quoted: false,
    warranty: "",
    warrantyInfo: "",
    serviceNotes: "",
    technicianInfo: "",
    paymentTermId: "",
    paymentMethodId: "",
    financialCategoryId: "",
    warehouseId: "",
  };
}

export function serviceOrderFormFromView(o: ServiceOrderView): ServiceOrderForm {
  const s = (v: string | null) => v ?? "";
  return {
    contact: { id: o.contact.id, name: o.contact.name },
    sellerId: o.seller?.id ?? "",
    storeId: o.store?.id ?? "",
    technician: s(o.technician),
    equipment: s(o.equipment),
    serialNumber: s(o.serialNumber),
    problem: s(o.problem),
    receptionNotes: s(o.receptionNotes),
    internalNotes: s(o.internalNotes),
    entryDate: o.entryDate,
    expectedDate: s(o.expectedDate),
    finishDate: s(o.finishDate),
    exitDate: s(o.exitDate),
    startTime: s(o.startTime),
    endTime: s(o.endTime),
    items: o.items.map((i) => ({
      key: osRowKey(),
      kind: i.kind,
      productId: i.productId,
      description: i.description,
      code: s(i.code),
      unit: s(i.unit),
      qtyQ4: i.qtyQ4,
      unitPriceCents: i.unitPriceCents,
      toQuote: i.toQuote,
      done: i.done,
    })),
    freightCents: o.freightCents || null,
    discountCents: o.discountCents || null,
    quoteMode: o.quoteMode,
    quoted: o.quoted,
    warranty: s(o.warranty),
    warrantyInfo: s(o.warrantyInfo),
    serviceNotes: s(o.serviceNotes),
    technicianInfo: s(o.technicianInfo),
    paymentTermId: o.paymentTerm?.id ?? "",
    paymentMethodId: o.paymentMethod?.id ?? "",
    financialCategoryId: o.financialCategory?.id ?? "",
    warehouseId: o.warehouse?.id ?? "",
  };
}

const orNull = (v: string) => (v.trim() ? v : null);

/** Corpo do POST/PUT. Na peça, descrição/código vêm do cadastro (não vão). */
export function serviceOrderBody(f: ServiceOrderForm): ServiceOrderFormValues {
  return {
    contactId: f.contact?.id ?? "",
    sellerId: orNull(f.sellerId),
    storeId: orNull(f.storeId),
    technician: f.technician,
    equipment: f.equipment,
    serialNumber: f.serialNumber,
    problem: f.problem,
    receptionNotes: f.receptionNotes,
    internalNotes: f.internalNotes,
    entryDate: f.entryDate,
    expectedDate: f.expectedDate,
    finishDate: f.finishDate,
    exitDate: f.exitDate,
    startTime: f.startTime,
    endTime: f.endTime,
    items: f.items.map((i) => ({
      kind: i.kind,
      productId: i.kind === "part" ? i.productId : null,
      description: i.kind === "service" ? i.description : null,
      code: i.kind === "service" ? i.code : null,
      qtyQ4: i.qtyQ4 ?? 0,
      unitPriceCents: i.unitPriceCents,
      toQuote: i.toQuote,
      done: i.done,
    })),
    freightCents: f.freightCents ?? 0,
    discountCents: f.discountCents ?? 0,
    quoteMode: f.quoteMode,
    quoted: f.quoted,
    warranty: f.warranty,
    warrantyInfo: f.warrantyInfo,
    serviceNotes: f.serviceNotes,
    technicianInfo: f.technicianInfo,
    paymentTermId: orNull(f.paymentTermId),
    paymentMethodId: orNull(f.paymentMethodId),
    financialCategoryId: orNull(f.financialCategoryId),
    warehouseId: orNull(f.warehouseId),
  } as ServiceOrderFormValues;
}

/**
 * Totais na tela (mesma regra do servidor, `serviceOrderTotals`). `null` = ainda incompleto (quantidade ou preço
 * vazio) ou inválido; `error` = a mensagem da regra (ex.: desconto acima dos itens).
 */
export function formTotals(f: ServiceOrderForm): { totals: ServiceOrderTotals | null; error: string | null } {
  if (f.items.some((i) => !i.qtyQ4 || i.unitPriceCents === null)) return { totals: null, error: null };
  try {
    return {
      totals: serviceOrderTotals({
        items: f.items.map((i) => ({ kind: i.kind, qtyQ4: i.qtyQ4!, unitPriceCents: i.unitPriceCents! })),
        freightCents: f.freightCents ?? 0,
        discountCents: f.discountCents ?? 0,
      }),
      error: null,
    };
  } catch (e) {
    return { totals: null, error: e instanceof Error ? e.message : String(e) };
  }
}

/** Erro do item `index` vindo da validação da API (`items.3.description` etc.). */
export function osItemError(errors: Record<string, string>, index: number): string | undefined {
  const prefix = `items.${index}.`;
  const k = Object.keys(errors).find((x) => x.startsWith(prefix));
  return k ? errors[k] : undefined;
}

/** Horas (q4) por extenso: 1,5 h. */
export const hoursLabel = (q4: number) =>
  `${(q4 / 10_000).toLocaleString("pt-BR", { maximumFractionDigits: 4 })} h`;

/** Resumo das notas pedidas no finalizar/emitir: o que saiu e o que ficou em rascunho (com o motivo). */
export function invoicesSummary(r: ServiceOrderInvoicesResult): { ok: string[]; problems: string[] } {
  const ok: string[] = [];
  const problems: string[] = [];
  for (const [name, o] of [
    ["NFS-e", r.nfse],
    ["NF-e", r.nfe],
  ] as const) {
    if (!o) continue;
    if (o.error) problems.push(`${name}: ${o.error}`);
    else ok.push(`${name} enviada`);
  }
  return { ok, problems };
}
